import React from 'react';
import { useShop } from '../context/ShopContext';
import { formatINR } from '../data/products';
import { 
  TrendingUp, 
  TrendingDown, 
  RefreshCw, 
  ShieldCheck, 
  Calculator, 
  LineChart, 
  Sparkles, 
  Clock, 
  ChevronRight,
  Info
} from 'lucide-react';

export default function LiveRatesSection({ onOpenCalculator, onOpenChart }) {
  const { 
    liveGoldRates, 
    masterMetalRates, 
    rateApiSettings,
    autoRefreshCountdown,
    isRefreshingRates,
    refreshLiveRates
  } = useShop();

  // Resolve rates dynamically
  const rate24K = masterMetalRates?.gold?.find((g) => g.label?.includes('24') || g.id?.includes('24'))?.rate || liveGoldRates?.gold24K || liveGoldRates?.gold24k || 7380;
  const rate22K = masterMetalRates?.gold?.find((g) => g.label?.includes('22') || g.id?.includes('22'))?.rate || liveGoldRates?.gold22K || liveGoldRates?.gold22k || 6765;
  const rate18K = masterMetalRates?.gold?.find((g) => g.label?.includes('18') || g.id?.includes('18'))?.rate || liveGoldRates?.gold18K || liveGoldRates?.gold18k || 5535;
  const rateSilver = masterMetalRates?.silver?.find((s) => s.label?.includes('999') || s.id?.includes('999'))?.rate || liveGoldRates?.silver999 || 89.50;

  const rateChange = liveGoldRates?.change || liveGoldRates?.rateChange || '+0.42%';
  const isPositiveChange = !rateChange.startsWith('-');

  const lastUpdated = liveGoldRates?.lastUpdated || `Updated Today, ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} IST`;
  const apiSource = rateApiSettings?.apiSource === 'ibja_benchmark' ? 'IBJA Benchmark (India Bullion & Jewellers Association)' : 'Indian Bullion Market Benchmark Spot';

  const scrollToCalculator = () => {
    if (onOpenCalculator) {
      onOpenCalculator();
    } else {
      const el = document.getElementById('jewellery-calculator-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToChart = () => {
    if (onOpenChart) {
      onOpenChart();
    } else {
      const el = document.getElementById('gold-silver-price-chart');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="live-bullion-rates-section"
      className="live-bullion-rates-section" 
      style={{
        padding: '1.5rem 1rem 1.8rem',
        background: '#ffffff',
        borderBottom: '1px solid #e0e0e0',
      }}
    >
      <div className="container" style={{ maxWidth: '1240px', margin: '0 auto' }}>
        
        {/* Header Ribbon: Title, Live Indicator, Auto-refresh Countdown, and Source */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.8rem',
          marginBottom: '1.2rem',
          paddingBottom: '0.8rem',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap' }}>
              <h2 style={{
                margin: 0,
                fontSize: 'clamp(1.2rem, 2.2vw, 1.55rem)',
                fontWeight: 800,
                color: '#212121',
                fontFamily: "'Playfair Display', serif, Georgia",
                letterSpacing: '-0.01em'
              }}>
                Live Gold & Silver Rates
              </h2>

              {/* Pulsing LIVE Indicator Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                background: '#e8f5e9',
                border: '1px solid #81c784',
                color: '#2e7d32',
                padding: '0.2rem 0.55rem',
                borderRadius: '12px',
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.04em',
                textTransform: 'uppercase'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#2e7d32',
                  boxShadow: '0 0 8px #2e7d32',
                  animation: 'pulse 1.8s infinite'
                }} />
                <span>LIVE</span>
              </div>

              {/* Today's Change Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                background: isPositiveChange ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${isPositiveChange ? '#bbf7d0' : '#fecaca'}`,
                color: isPositiveChange ? '#15803d' : '#b91c1c',
                padding: '0.2rem 0.55rem',
                borderRadius: '4px',
                fontSize: '0.74rem',
                fontWeight: 700
              }}>
                {isPositiveChange ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                <span>{rateChange} Today</span>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.7rem',
              marginTop: '0.35rem',
              fontSize: '0.76rem',
              color: '#666',
              flexWrap: 'wrap'
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                <Clock size={12} color="#888" />
                {lastUpdated}
              </span>
              <span>•</span>
              <span style={{ color: '#2874f0', fontWeight: 600 }}>
                {apiSource}
              </span>
            </div>
          </div>

          {/* Right Action Tools: Manual Refresh, Calculator, Chart */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {/* Auto Refresh Status & Countdown */}
            <button
              onClick={() => refreshLiveRates(false)}
              disabled={isRefreshingRates}
              title="Click to refresh live bullion rates now"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                padding: '0.42rem 0.75rem',
                fontSize: '0.76rem',
                fontWeight: 600,
                color: '#475569',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <RefreshCw 
                size={13} 
                style={{ 
                  animation: isRefreshingRates ? 'spin 1s linear infinite' : 'none',
                  color: isRefreshingRates ? '#2874f0' : '#64748b'
                }} 
              />
              <span>{isRefreshingRates ? 'Updating...' : `Auto: ${autoRefreshCountdown}s`}</span>
            </button>

            <button
              onClick={scrollToCalculator}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                background: '#ff9f00',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                padding: '0.42rem 0.85rem',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(255, 159, 0, 0.25)'
              }}
            >
              <Calculator size={14} />
              <span>Rate Calculator</span>
            </button>

            <button
              onClick={scrollToChart}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                background: '#2874f0',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                padding: '0.42rem 0.85rem',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(40, 116, 240, 0.25)'
              }}
            >
              <LineChart size={14} />
              <span>Price Trend Chart</span>
            </button>
          </div>
        </div>

        {/* 4 Premium Cards Grid: 24K Gold, 22K Gold, 18K Gold, 999 Silver */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Card 1: 24K / 999 Pure Gold */}
          <div style={{
            background: 'linear-gradient(145deg, #fffcf0 0%, #ffffff 100%)',
            border: '1.5px solid #e6ca65',
            borderRadius: '8px',
            padding: '1.1rem 1.2rem',
            position: 'relative',
            boxShadow: '0 4px 12px rgba(184, 134, 11, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#b8860b' }} />
                <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#8a6d1c', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  24K Gold (999)
                </span>
              </div>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#2e7d32', background: '#e8f5e9', padding: '0.12rem 0.45rem', borderRadius: '3px' }}>
                Pure Bullion
              </span>
            </div>

            <div>
              <div style={{ fontSize: '1.55rem', fontWeight: 900, color: '#212121', lineHeight: 1.1 }}>
                {formatINR(rate24K)}
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', marginLeft: '0.25rem' }}>/ gram</span>
              </div>
              <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#b8860b', marginTop: '0.25rem' }}>
                {formatINR(Math.round(rate24K * 10))} <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#777' }}>per 10 grams</span>
              </div>
            </div>

            <div style={{ marginTop: '0.9rem', paddingTop: '0.65rem', borderTop: '1px dashed #e6ca65', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', color: '#666' }}>
              <span>Purity: 99.9% Hallmark</span>
              <button 
                onClick={scrollToCalculator}
                style={{ background: 'none', border: 'none', color: '#2874f0', fontWeight: 700, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: '2px' }}
              >
                Calculate <ChevronRight size={12} />
              </button>
            </div>
          </div>

          {/* Card 2: 22K / 916 Standard Jewellery Gold */}
          <div style={{
            background: 'linear-gradient(145deg, #fffdf5 0%, #ffffff 100%)',
            border: '2px solid #ff9f00',
            borderRadius: '8px',
            padding: '1.1rem 1.2rem',
            position: 'relative',
            boxShadow: '0 6px 16px rgba(255, 159, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div style={{
              position: 'absolute',
              top: -10,
              right: 14,
              background: '#ff9f00',
              color: '#ffffff',
              fontSize: '0.62rem',
              fontWeight: 800,
              padding: '0.12rem 0.5rem',
              borderRadius: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.04em'
            }}>
              Most Popular
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ff9f00' }} />
                <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  22K Gold (916)
                </span>
              </div>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#b45309', background: '#fef3c7', padding: '0.12rem 0.45rem', borderRadius: '3px' }}>
                BIS 916
              </span>
            </div>

            <div>
              <div style={{ fontSize: '1.55rem', fontWeight: 900, color: '#212121', lineHeight: 1.1 }}>
                {formatINR(rate22K)}
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', marginLeft: '0.25rem' }}>/ gram</span>
              </div>
              <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#d97706', marginTop: '0.25rem' }}>
                {formatINR(Math.round(rate22K * 10))} <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#777' }}>per 10 grams</span>
              </div>
            </div>

            <div style={{ marginTop: '0.9rem', paddingTop: '0.65rem', borderTop: '1px dashed #fed7aa', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', color: '#666' }}>
              <span>Purity: 91.6% Hallmark</span>
              <button 
                onClick={scrollToCalculator}
                style={{ background: 'none', border: 'none', color: '#2874f0', fontWeight: 700, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: '2px' }}
              >
                Calculate <ChevronRight size={12} />
              </button>
            </div>
          </div>

          {/* Card 3: 18K / 750 Diamond Jewellery Gold */}
          <div style={{
            background: 'linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)',
            border: '1.5px solid #cbd5e1',
            borderRadius: '8px',
            padding: '1.1rem 1.2rem',
            position: 'relative',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#64748b' }} />
                <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  18K Gold (750)
                </span>
              </div>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#0284c7', background: '#e0f2fe', padding: '0.12rem 0.45rem', borderRadius: '3px' }}>
                Diamond Craft
              </span>
            </div>

            <div>
              <div style={{ fontSize: '1.55rem', fontWeight: 900, color: '#212121', lineHeight: 1.1 }}>
                {formatINR(rate18K)}
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', marginLeft: '0.25rem' }}>/ gram</span>
              </div>
              <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#475569', marginTop: '0.25rem' }}>
                {formatINR(Math.round(rate18K * 10))} <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#777' }}>per 10 grams</span>
              </div>
            </div>

            <div style={{ marginTop: '0.9rem', paddingTop: '0.65rem', borderTop: '1px dashed #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', color: '#666' }}>
              <span>Purity: 75.0% Hallmark</span>
              <button 
                onClick={scrollToCalculator}
                style={{ background: 'none', border: 'none', color: '#2874f0', fontWeight: 700, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: '2px' }}
              >
                Calculate <ChevronRight size={12} />
              </button>
            </div>
          </div>

          {/* Card 4: 999 Pure Silver */}
          <div style={{
            background: 'linear-gradient(145deg, #f1f5f9 0%, #ffffff 100%)',
            border: '1.5px solid #94a3b8',
            borderRadius: '8px',
            padding: '1.1rem 1.2rem',
            position: 'relative',
            boxShadow: '0 4px 12px rgba(100, 116, 139, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }} />
                <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  999 Pure Silver
                </span>
              </div>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', padding: '0.12rem 0.45rem', borderRadius: '3px' }}>
                Bullion & Utensils
              </span>
            </div>

            <div>
              <div style={{ fontSize: '1.55rem', fontWeight: 900, color: '#212121', lineHeight: 1.1 }}>
                ₹{rateSilver}
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', marginLeft: '0.25rem' }}>/ gram</span>
              </div>
              <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#1e3a8a', marginTop: '0.25rem' }}>
                {formatINR(Math.round(rateSilver * 1000))} <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#777' }}>per 1 Kilogram</span>
              </div>
            </div>

            <div style={{ marginTop: '0.9rem', paddingTop: '0.65rem', borderTop: '1px dashed #94a3b8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', color: '#666' }}>
              <span>Purity: 99.9% Fine Silver</span>
              <button 
                onClick={scrollToCalculator}
                style={{ background: 'none', border: 'none', color: '#2874f0', fontWeight: 700, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: '2px' }}
              >
                Calculate <ChevronRight size={12} />
              </button>
            </div>
          </div>
        </div>

        {/* Small Notice & Compliance Bar */}
        <div style={{
          background: '#f8fafc',
          borderRadius: '4px',
          padding: '0.55rem 0.85rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          fontSize: '0.73rem',
          color: '#64748b',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <ShieldCheck size={14} color="#16a34a" />
            <span>
              All rates strictly benchmarked against official bullion spot indices. 100% BIS Hallmarked HUID guaranteed on all deliveries.
            </span>
          </div>
          <span style={{ fontWeight: 600, color: '#475569' }}>
            GST: 3% Applicable on Bullion & Jewellery
          </span>
        </div>
      </div>
    </section>
  );
}
